// /my-barn/login — the BARN's front door.
//
// The Sale Barn App and My Barn desktop are one product, sold to the barn, and
// deliberately separate from Market Intelligence (owner decision 2026-08-16).
// A barn signs in HERE with the same email that reaches their app; they land in
// My Barn and that is their whole world. Nobody who comes through this door gets
// the market intelligence platform.
//
// WHY A SECOND DOOR RATHER THAN A THIRD GUARD.
// Until now both products shared /auth, and two pieces of code existed purely to
// shove barn users back out of the platform afterwards — BarnOnlyLock, which
// runs on EVERY navigation, and a special case inside TierGate. Guards that undo
// a journey the product already let someone start. It also caused a real bug:
// stale `from` state on the shared /auth entry dropped a platform admin onto a
// barn's dashboard (fixed 2026-08-16, and this removes the class of it).
//
// Separating the doors makes "a barn account cannot reach the platform" true by
// construction. The guard stays as a backstop for someone typing a URL, but it
// is no longer the thing holding the products apart.
//
// ROLES: owners and managers (MY_BARN_ROLES). Staff work the app, not the
// dashboard — signing in here without one of those roles is refused, with the
// session ended, rather than half-admitted to a screen with nothing on it.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Warehouse } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Does this signed-in account own or manage a barn? Asked directly rather than
 *  through the hook so the answer is fresh at the moment of sign-in — a stale
 *  cached "no" would bounce a legitimate barn owner. */
async function hasBarnRole(): Promise<boolean> {
  const { data, error } = await supabase.rpc("barns_for_user", {});
  if (error) return false;
  const rows: { role?: string }[] = Array.isArray(data) ? data : [];
  return rows.some((r) => r?.role === "owner" || r?.role === "manager");
}

export default function MyBarnLogin() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useUserProfile();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Already signed in and entitled → straight through. Signed in WITHOUT a barn
  // role means they came to the wrong door; say so instead of showing an empty
  // dashboard.
  useEffect(() => {
    if (authLoading || !session || busy) return;
    let cancelled = false;
    setChecking(true);
    void hasBarnRole().then((ok) => {
      if (cancelled) return;
      setChecking(false);
      if (ok) navigate("/my-barn", { replace: true });
    });
    return () => { cancelled = true; };
  }, [authLoading, session, busy, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        setErr("That email and password don't match. Check them and try again.");
        return;
      }
      if (await hasBarnRole()) {
        navigate("/my-barn", { replace: true });
        return;
      }
      // A real account, but not a barn owner or manager. End the session rather
      // than leave them signed in at a door that leads nowhere for them.
      await supabase.auth.signOut();
      setErr(
        "That login isn't set up for a sale barn dashboard. If you're a barn owner or manager, ask us to add you.",
      );
    } finally {
      setBusy(false);
    }
  };

  const waiting = authLoading || checking;

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
      <div className="mb-7 text-center">
        <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Warehouse strokeWidth={2} />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight">My Barn</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your barn's own dashboard — sale numbers, consignments and buyers. Sign in with the
          same email you use for your barn's app.
        </p>
      </div>

      {waiting ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mb-email">Email</Label>
            <Input
              id="mb-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbarn.com"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mb-password">Password</Label>
            <Input
              id="mb-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {err && <p className="text-sm font-medium text-destructive">{err}</p>}

          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in to My Barn"}
          </button>
        </form>
      )}

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Barn dashboards are set up by RaisedBeef — there's no sign-up here. Need access?{" "}
        <a className="underline" href="mailto:contact@raisedbeef.com">Get in touch</a>.
      </p>
    </div>
  );
}
